/**
 * Parallax Scroll Effect
 * IntersectionObserver + requestAnimationFrame で効率的にパララックス実装
 *
 * 使用方法:
 *   1. section に data-parallax 属性を付与
 *   2. スクロール時に動かす要素に data-parallax-target 属性を付与
 *   3. section に CSS で --parallax-amount: <px> を宣言 (省略時 100px)
 *
 * HTML例:
 *   <section data-parallax>
 *     <div data-parallax-target>
 *       <!-- スクロール時にこの要素が Y軸で移動 -->
 *     </div>
 *   </section>
 *
 * CSS例 (祖先要素で --parallax-amount を宣言するだけ。バッファ枠の position/top/height は
 * frontend/styles/components/parallax.css が [data-parallax-target] に自動付与する):
 *   .c-image-showcase {
 *     --parallax-amount: 100px;
 *   }
 *
 * パララックス効果の仕組み:
 *   - CSS で要素を上下に大きく配置 (top: -A, height: calc(100% + 2A))
 *   - スクロール時に -A → +A に translateY で移動
 *   - セクションが画面に入ってる間だけ RAF でループ実行
 */

const DEFAULT_PARALLAX_AMOUNT = 100;
const PARALLAX_ATTR = 'data-parallax';
const PARALLAX_TARGET_ATTR = 'data-parallax-target';

/**
 * @typedef {Object} ParallaxEntry
 * @property {HTMLElement} target
 * @property {number} amount
 */

/**
 * @returns {void}
 */
export function initParallax() {
  const sections = [...document.querySelectorAll(`[${PARALLAX_ATTR}]`)];
  if (!sections.length) return;

  /** @type {Map<HTMLElement, ParallaxEntry>} */
  const active = new Map();
  let running = false;
  /** @type {number|null} */
  let rafId = null;

  /**
   * @param {HTMLElement} section
   * @returns {number}
   */
  function readAmount(section) {
    const styles = window.getComputedStyle(section);
    const value = parseFloat(styles.getPropertyValue('--parallax-amount'));
    return Number.isFinite(value) && value > 0
      ? value
      : DEFAULT_PARALLAX_AMOUNT;
  }

  /**
   * @param {HTMLElement} section
   * @param {ParallaxEntry} entry
   * @param {number} windowH
   * @returns {void}
   */
  function updateParallaxForSection(section, entry, windowH) {
    const { target, amount } = entry;
    const maxMove = amount * 2;

    const rect = section.getBoundingClientRect();
    const sectionHeight = rect.height || section.offsetHeight;

    const start = windowH;
    const end = -sectionHeight;
    const progressRaw = (rect.top - start) / (end - start);
    const progress = Math.min(1, Math.max(0, progressRaw));

    const moveY = -amount + maxMove * progress;

    target.style.transform = `translate3d(0, ${moveY}px, 0)`;
  }

  function tick() {
    if (!running) return;
    const windowH = window.innerHeight || document.documentElement.clientHeight;
    active.forEach((entry, section) => {
      updateParallaxForSection(section, entry, windowH);
    });
    rafId = window.requestAnimationFrame(tick);
  }

  function startLoop() {
    if (running) return;
    running = true;
    rafId = window.requestAnimationFrame(tick);
  }

  function stopLoop() {
    running = false;
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const section = /** @type {HTMLElement} */ (entry.target);
        if (entry.isIntersecting) {
          /** @type {HTMLElement|null} */
          const target = section.querySelector(`[${PARALLAX_TARGET_ATTR}]`);
          if (!target) return;
          // 可視化のたびに CSS 変数を読み直す (media query 越境後の値変化に追従)
          active.set(section, { target, amount: readAmount(section) });
          if (active.size === 1) startLoop();
        } else {
          active.delete(section);
          if (active.size === 0) stopLoop();
        }
      });
    },
    { root: null, threshold: 0 },
  );

  sections.forEach(section => observer.observe(section));

  window.addEventListener('resize', () => {
    active.forEach((entry, section) => {
      const next = readAmount(section);
      if (next === entry.amount) return;
      entry.amount = next;
    });
  });
}
